import { useState } from 'react'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import { Ltr } from './Ltr'

interface Props {
  value: string
  /** Characters to show. The rest stays reachable through copy and tooltip. */
  visible?: number
  label?: string
}

/**
 * A long technical identifier in a narrow column.
 *
 * UUIDs and client ids are what an operator quotes in a support conversation,
 * so they cannot be dropped — but shown in full they push every useful column
 * off the screen. Shortened *and* copyable is the compromise: the full value is
 * one click away and never lost.
 *
 * Rendered dir="ltr": a hex identifier inside a right-to-left line otherwise
 * has its segments reordered, which makes it wrong to read aloud.
 */
export function CopyableId({ value, visible = 8, label }: Props) {
  const [copied, setCopied] = useState(false)

  const shortened = value.length > visible ? `${value.slice(0, visible)}…` : value

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access can be refused; the tooltip still carries the value,
      // so failing silently leaves the operator no worse off.
    }
  }

  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }} component="span">
      <Tooltip title={label ? `${label}: ${value}` : value}>
        <Ltr component="span"
          variant="body2"
          sx={{ fontFamily: 'monospace' }}
        >
          {shortened}
        </Ltr>
      </Tooltip>
      <Tooltip title={copied ? 'کپی شد' : 'کپی کردن'}>
        <IconButton size="small" onClick={() => void copy()} aria-label={`کپی کردن ${label ?? 'شناسه'}`}>
          {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Stack>
  )
}
